import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	effect,
	ElementRef,
	inject,
	Injector,
	input,
	NgZone,
	OnDestroy,
	viewChild,
	output,
} from '@angular/core';

import * as d3 from 'd3';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { BarTextStyle, GanttSegment, ParameterValueType } from './gantt.models';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { DateFormatOptions, TranslocoDatePipe } from '@jsverse/transloco-locale';
import { DateUtil } from '../../../util/DateUtil';
import { MatDialog } from '@angular/material/dialog';
import { OverlapDialogComponent } from './overlap-dialog/overlap-dialog.component';
import { ColorHelper } from '../../../services/ColorHelper';

type TimeScale = d3.ScaleTime<number, number>;

const d3GermanLocale = d3.timeFormatLocale({
	dateTime: '%A, der %e. %B %Y, %X',
	date: '%d.%m.%Y',
	time: '%H:%M:%S',
	periods: ['AM', 'PM'],
	days: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
	shortDays: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
	months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
	shortMonths: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
});

const formatMonth = d3GermanLocale.format('%b'); // e.g., "Jan"
const formatYear = d3GermanLocale.format("%b '%y"); // e.g., "Jan '24"

@Component({
	selector: 'ara-bar-chart',
	imports: [OverlayModule, TranslocoDirective, TranslocoDatePipe],
	templateUrl: './ara-bar-chart.component.html',
	styleUrls: ['./ara-bar-chart.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AraBarChartComponent implements AfterViewInit, OnDestroy {
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly dialog = inject(MatDialog);
	private readonly injector = inject(Injector);

	// Static counter for generating unique IDs (safe for non-cryptographic purposes)
	private static instanceCounter = 0;

	public readonly shortDateOptions: DateFormatOptions = DateUtil.localizedNumericDateOptions;
	private readonly translocoService = inject(TranslocoService);

	data = input.required<GanttSegment[]>();
	viewStartDate = input<Date | null | undefined>(null);
	viewEndDate = input<Date | null | undefined>(null);
	overlaps = input<GanttSegment[]>([]); // ADDED: Input for overlaps
	xScale = input.required<TimeScale>();
	height = input<number>(70);
	showAxis = input<boolean>(false);
	axisPosition = input<'top' | 'bottom' | 'none'>('bottom');
	axisOffset = input<number>(8);
	barHeight = input<number>(24);
	barTextStyle = input<BarTextStyle>({});
	animationDuration = input<number>(700);
	barOverflowAmount = input<number>(5);
	allowSegmentClick = input<boolean>(true);
	parameterValueType = input<ParameterValueType>();

	readonly segmentClick = output<GanttSegment>();
	readonly dateRangeShift = output<{
		startDate: Date;
		endDate: Date;
	}>();
	readonly dragStart = output<void>();
	readonly dragEnd = output<void>();

	// Enable drag on axis (timeline)
	enableAxisDrag = input<boolean>(false);
	// External flag to disable animation (e.g., during drag from another component)
	disableAnimation = input<boolean>(false);

	// --- VIEW REFERENCES ---
	// Not .required: this signal is read inside a reactive effect/computed that runs before the
	// view query resolves, so .required would throw NG0951. Reads already guard with ?./if().
	private readonly svg = viewChild<ElementRef<SVGElement>>('svgContainer');

	// --- PUBLIC STATE for CDK Overlay ---
	public isTooltipVisible = false;
	public selectedSegment: GanttSegment | null = null;
	public tooltipPosition = { x: '0px', y: '0px' };

	// --- Configuration for the overlay's position relative to the cursor ---
	public overlayPositions: ConnectedPosition[] = [
		{ originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
		{ originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
		{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
		{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 },
	];

	// --- PRIVATE D3 PROPERTIES ---
	private g!: d3.Selection<SVGGElement, unknown, null, undefined>;
	private width = 0;
	private readonly instanceId = ++AraBarChartComponent.instanceCounter;
	private readonly clipPathId = `ara-chart-clip-${this.instanceId}`;
	private readonly overlapPatternId = `overlap-pattern-${this.instanceId}`;

	private resizeObserver!: ResizeObserver;

	// Drag state for axis
	private isDragging = false;
	private dragStartX = 0;
	private dragStartDate: Date | null = null;
	private dragEndDate: Date | null = null;
	private dragScale: TimeScale | null = null;
	private lastEmitTime = 0;
	private readonly THROTTLE_MS = 50; // Emit max every 50ms for smooth updates
	private readonly ngZone = inject(NgZone);

	// Bound event handlers for cleanup
	private boundMouseMove: ((e: MouseEvent) => void) | null = null;
	private boundMouseUp: ((e: MouseEvent) => void) | null = null;

	/**
	 * Gets the primary color from CSS variables as RGB string
	 */
	private getPrimaryColor(): string {
		const el = this.svg()?.nativeElement;
		if (!el) return '219, 0, 49'; // Fallback
		const colorVar = getComputedStyle(el).getPropertyValue('--color-primary').trim();
		return colorVar || '219, 0, 49';
	}

	constructor() {
		effect(
			() => {
				this.data();
				this.xScale();
				if (this.svg()) {
					setTimeout(() => this.createChart(), 0);
				}
			},
			{ injector: this.injector }
		);
	}

	ngAfterViewInit(): void {
		this.resizeObserver = new ResizeObserver(() => {
			// This callback fires whenever the component's container resizes.
			// We simply call createChart() to force a complete redraw.
			this.createChart();
		});

		// We observe the parent element of the SVG, as that's what defines the available space.
		const parentElement = this.svg()?.nativeElement.parentElement;
		if (parentElement) {
			this.resizeObserver.observe(parentElement);
		}
	}

	ngOnDestroy(): void {
		if (this.resizeObserver) {
			// Disconnect the observer to prevent memory leaks
			this.resizeObserver.disconnect();
		}
		// Clean up drag event listeners
		this.removeDragListeners();
	}

	private removeDragListeners(): void {
		if (this.boundMouseMove) {
			document.removeEventListener('mousemove', this.boundMouseMove);
			this.boundMouseMove = null;
		}
		if (this.boundMouseUp) {
			document.removeEventListener('mouseup', this.boundMouseUp);
			this.boundMouseUp = null;
		}
	}

	private onSegmentClick(event: MouseEvent, segment: GanttSegment): void {
		if (this.allowSegmentClick()) {
			event.stopPropagation();
			if (segment.isOverlap && segment.underlyingSegments?.length > 0) {
				this.openOverlapDialog(segment.underlyingSegments);
			} else if (!segment.isOverlap) {
				// It's a regular segment, emit immediately
				this.segmentClick.emit(segment);
			}
		}
	}

	private openOverlapDialog(segments: GanttSegment[]): void {
		const dialogRef = this.dialog.open(OverlapDialogComponent, {
			width: '400px',
			data: { segments: segments, parameterValueType: this.parameterValueType() },
		});

		dialogRef.afterClosed().subscribe((selectedSegment) => {
			// If the user selected a segment (and didn't just click cancel)
			if (selectedSegment) {
				this.segmentClick.emit(selectedSegment);
			}
		});
	}

	private createChart(): void {
		const element = this.svg().nativeElement;
		d3.select(element).selectAll('*').remove();

		// This width is the full width of the container
		this.width = element.clientWidth; // Use clientWidth directly

		// Define vertical margins for positioning the axis
		const verticalMargin = { top: 10, bottom: 10 };
		if (this.showAxis()) {
			if (this.axisPosition() === 'top') {
				verticalMargin.top = 30;
			} else {
				verticalMargin.bottom = 30;
			}
		}
		const svg = d3.select(element).attr('width', this.width).attr('height', this.height());

		this.g = svg.append('g').attr('transform', `translate(0, ${verticalMargin.top})`);

		const innerHeight = this.height() - verticalMargin.top - verticalMargin.bottom;
		const barY = (innerHeight - this.barHeight()) / 2;

		const scaleRange = this.xScale().range();

		// Calculate clip area to include bars that extend beyond innerHeight (negative barY)
		const clipY = Math.min(barY, 0);
		const clipHeight = Math.max(innerHeight, this.barHeight()) + Math.abs(clipY);

		const defs = svg.append('defs');

		// ClipPath for chart area
		defs.append('clipPath')
			.attr('id', this.clipPathId)
			.append('rect')
			.attr('x', scaleRange[0] - this.barOverflowAmount())
			.attr('y', clipY) // Start from negative Y if bar extends above
			.attr('width', scaleRange[1] - scaleRange[0] + 2 * this.barOverflowAmount())
			.attr('height', clipHeight);

		// Overlap pattern - bold diagonal stripes for conflict visibility
		const primaryColor = this.getPrimaryColor();
		const stripeWidth = 6;
		const stripeGap = 6;
		const patternSize = stripeWidth + stripeGap;

		const pattern = defs
			.append('pattern')
			.attr('id', this.overlapPatternId)
			.attr('patternUnits', 'userSpaceOnUse')
			.attr('width', patternSize)
			.attr('height', patternSize)
			.attr('patternTransform', 'rotate(-45)');

		// White background
		pattern.append('rect').attr('width', patternSize).attr('height', patternSize).attr('fill', 'white');

		// Bold primary-colored stripe
		pattern.append('rect').attr('x', 0).attr('y', 0).attr('width', stripeWidth).attr('height', patternSize).attr('fill', `rgba(${primaryColor}, 0.6)`);

		this.g = svg.append('g').attr('transform', `translate(0, ${verticalMargin.top})`).attr('clip-path', `url(#${this.clipPathId})`);

		this.drawConnectorLine(barY);
		this.drawBars(barY);
		this.drawOverlaps(barY);
		this.drawLabels(barY);
		this.drawOverflowIndicators(barY);

		if (this.showAxis()) {
			const axisGroupContainer = svg.append('g').attr('transform', `translate(0, ${verticalMargin.top})`);
			this.drawAxis(innerHeight, axisGroupContainer);
		}
	}

	/**
	 * Checks if validTo should be hidden (is maxDate or null/undefined)
	 */
	shouldHideToDate(date: Date): boolean {
		if (!date) return true;
		return DateUtil.maxDate.isSame(date, 'day');
	}

	/**
	 * Checks if validFrom is a "permanent" date (null or 1970-01-01)
	 */
	isFromDatePermanent(date: Date): boolean {
		if (!date) return true;
		return DateUtil.minDate.isSame(date, 'day');
	}

	private drawConnectorLine(barY: number): void {
		const scale = this.xScale();
		const scaleRange = scale.range();
		const data = this.data();
		const lineY = barY + this.barHeight() / 2;

		// If no data, draw a full grey line across the entire range
		if (!data || data.length === 0) {
			this.g
				.append('line')
				.attr('class', 'connector-line')
				.attr('x1', scaleRange[0])
				.attr('y1', lineY)
				.attr('x2', scaleRange[1])
				.attr('y2', lineY)
				.attr('stroke', '#cccccc')
				.attr('stroke-width', 2);
			return;
		}

		// Sort segments by start position
		const sortedSegments = [...data].sort((a, b) => a.from.getTime() - b.from.getTime());

		// Draw line segments only in gaps between bars
		let currentX = scaleRange[0];

		for (const segment of sortedSegments) {
			const segmentStart = scale(segment.from);
			const segmentEnd = scale(segment.to);

			// Draw line from current position to segment start (gap before this segment)
			if (segmentStart > currentX) {
				this.g
					.append('line')
					.attr('class', 'connector-line')
					.attr('x1', currentX)
					.attr('y1', lineY)
					.attr('x2', segmentStart)
					.attr('y2', lineY)
					.attr('stroke', '#cccccc')
					.attr('stroke-width', 2);
			}

			// Move current position to end of this segment
			currentX = Math.max(currentX, segmentEnd);
		}

		// Draw line from last segment to end (gap after last segment)
		if (currentX < scaleRange[1]) {
			this.g
				.append('line')
				.attr('class', 'connector-line')
				.attr('x1', currentX)
				.attr('y1', lineY)
				.attr('x2', scaleRange[1])
				.attr('y2', lineY)
				.attr('stroke', '#cccccc')
				.attr('stroke-width', 2);
		}
	}

	private drawBars(barY: number): void {
		const scale = this.xScale();
		const barHeight = this.barHeight();
		const styles = this.barTextStyle();
		// Completely disable animation during drag (local or external)
		const useAnimation = !this.isDragging && !this.disableAnimation() && this.animationDuration() > 0;
		const duration = this.animationDuration();

		const bars = this.g.selectAll('rect.bar-segment').data(this.data());

		// Enter: create new bars - set x and width IMMEDIATELY based on animation mode
		const enterBars = bars
			.enter()
			.append('rect')
			.attr('class', (d) => (d.color.includes('var(--color-primary)') ? 'bar-segment bar-segment-primary' : 'bar-segment'))
			// Set x and width immediately - this is the key fix!
			.attr('x', (d) => (useAnimation ? scale.range()[0] : scale(d.from)))
			.attr('width', (d) => (useAnimation ? 0 : Math.max(0, scale(d.to) - scale(d.from))))
			.attr('y', barY)
			.attr('height', barHeight)
			.attr('fill', (d) => d.color)
			.attr('rx', 3)
			.attr('ry', 3)
			.attr('font-size', styles.fontSize || '13px')
			.attr('font-family', styles.fontFamily || 'sans-serif')
			.style('cursor', [ParameterValueType.TENANT, ParameterValueType.ROLE].includes(this.parameterValueType()) ? 'default' : 'pointer')
			.on('mouseover', (event: MouseEvent, d: GanttSegment) => this.showTooltip(event, d))
			.on('mouseleave', () => this.hideTooltip())
			.on('click', (event: MouseEvent, d: GanttSegment) => this.onSegmentClick(event, d));

		// Only apply animation transition if needed
		if (useAnimation) {
			enterBars
				.transition()
				.duration(duration)
				.delay((d, i) => i * 20)
				.attr('x', (d) => scale(d.from))
				.attr('width', (d) => Math.max(0, scale(d.to) - scale(d.from)));
		}

		// Exit: remove old bars
		bars.exit().remove();
	}

	private drawLabels(barY: number): void {
		const scale = this.xScale();
		const barHeight = this.barHeight();
		const styles = this.barTextStyle();
		// Completely disable animation during drag (local or external)
		const useAnimation = !this.isDragging && !this.disableAnimation() && this.animationDuration() > 0;
		const duration = this.animationDuration();

		const labels = this.g.selectAll('text.bar-label').data(this.data());

		// Enter: create new labels
		const enterLabels = labels
			.enter()
			.append('text')
			.attr('class', 'bar-label')
			.attr('x', (d) => {
				const viewStartPx = scale.range()[0];
				const viewEndPx = scale.range()[1];
				const barStartPx = scale(d.from);
				const barEndPx = scale(d.to);
				const visibleStartX = Math.max(barStartPx, viewStartPx);
				const visibleEndX = Math.min(barEndPx, viewEndPx);
				return visibleStartX + (visibleEndX - visibleStartX) / 2;
			})
			.attr('y', barY + barHeight / 2)
			.attr('fill', (d) => {
				if (d.color.includes('var(--color-primary)')) {
					return 'rgb(var(--color-primary))';
				}
				return this.dynamicTextColorForBackground(d.color, 4.5);
			})
			.attr('font-size', styles.fontSize || '13px')
			.attr('font-weight', styles.fontWeight || '600')
			.attr('font-family', styles.fontFamily || 'sans-serif')
			.text((d) => {
				let text = d.value;
				if (d.unit) {
					text += ' ' + this.translocoService.translate('enum.' + d.unit.toUpperCase());
				} else if (['tenantPayment', 'tenantTarif'].includes(d.additionalDataType)) {
					text += ' €';
				}
				if (d.additionalDataType === 'tenantPayment' && d.additionalData?.periodicity) {
					text += ' ' + this.translocoService.translate('enum.' + d.additionalData.periodicity);
				}
				if (d.additionalDataType === 'tenantShareType' && d.additionalData) {
					text += ' ' + this.translocoService.translate('tenantShareType.' + d.additionalData);
				}
				return text;
			})
			.attr('display', function (d) {
				if (d.isOverlap) return 'none';
				const viewStartPx = scale.range()[0];
				const viewEndPx = scale.range()[1];
				const barStartPx = scale(d.from);
				const barEndPx = scale(d.to);
				const visibleStartX = Math.max(barStartPx, viewStartPx);
				const visibleEndX = Math.min(barEndPx, viewEndPx);
				const visibleWidth = visibleEndX - visibleStartX;
				const textWidth = this.getComputedTextLength();
				const padding = 12;
				return visibleWidth > textWidth + padding ? 'block' : 'none';
			});

		if (useAnimation) {
			// With animation: fade in
			enterLabels
				.attr('opacity', 0)
				.transition()
				.duration(duration / 2)
				.delay(duration / 2)
				.attr('opacity', 1);
		} else {
			// Without animation: immediately visible
			enterLabels.attr('opacity', 1);
		}

		// Exit: remove old labels
		labels.exit().remove();
	}

	/**
	 * Draws small left/right arrows inside bars when they extend beyond the visible range.
	 * Shows ∞ symbol next to the arrow when from=1970 or to=9999 (permanent dates).
	 * Skips indicators when they would overlap with the bar's text label.
	 */
	private drawOverflowIndicators(barY: number): void {
		const data = this.data();
		if (!data || data.length === 0) return;

		const scale = this.xScale();
		const [viewStart, viewEnd] = scale.range();
		const arrowSize = Math.min(5, this.barHeight() / 4);
		const centerY = barY + this.barHeight() / 2;
		const labelBounds = this.getVisibleLabelBounds();

		const segments = data.filter((s) => !s.isOverlap);
		for (const segment of segments) {
			const segStart = scale(segment.from);
			const segEnd = scale(segment.to);
			const visibleLeft = Math.max(segStart, viewStart);
			const visibleRight = Math.min(segEnd, viewEnd);
			if (visibleLeft >= visibleRight) continue;

			const color = this.getSegmentStrokeColor(segment);
			const lb = labelBounds.get(segment);
			const leftPermanent = segment.from.getFullYear() <= 1970;
			const rightPermanent = segment.to.getFullYear() >= 9999;

			if (segStart < viewStart) {
				this.drawEdgeIndicator(visibleLeft, centerY, arrowSize, 'left', segment.from, color, lb);
			} else if (leftPermanent) {
				this.drawPermanentIndicator(visibleLeft, centerY, 'left', color, lb);
			}
			if (segEnd > viewEnd) {
				this.drawEdgeIndicator(visibleRight, centerY, arrowSize, 'right', segment.to, color, lb);
			} else if (rightPermanent) {
				this.drawPermanentIndicator(visibleRight, centerY, 'right', color, lb);
			}
		}
	}

	private getSegmentStrokeColor(segment: GanttSegment): string {
		return segment.color.includes('var(--color-primary)') ? 'rgb(var(--color-primary))' : '#fff';
	}

	/**
	 * Draws an overflow arrow (and optional ∞) at one edge of a bar, if there is enough space.
	 */
	private drawEdgeIndicator(
		edgeX: number,
		centerY: number,
		arrowSize: number,
		direction: 'left' | 'right',
		date: Date,
		color: string,
		lb?: { left: number; right: number }
	): void {
		const isLeft = direction === 'left';
		const year = date.getFullYear();
		const isPermanent = isLeft ? year <= 1970 : year >= 9999;
		const indicatorSpace = 6 + arrowSize + (isPermanent ? 16 : 0);

		// Check if indicator would overlap with the label text
		if (lb) {
			const indicatorEdge = isLeft ? edgeX + indicatorSpace : edgeX - indicatorSpace;
			const overlaps = isLeft ? indicatorEdge >= lb.left - 4 : indicatorEdge <= lb.right + 4;
			if (overlaps) return;
		}

		const tipX = isLeft ? edgeX + 6 : edgeX - 6;
		this.drawOverflowArrow(tipX, centerY, arrowSize, direction, color);

		if (isPermanent) {
			const infX = isLeft ? tipX + arrowSize + 3 : tipX - arrowSize - 3;
			const anchor = isLeft ? 'start' : 'end';
			this.drawOverflowInfinity(infX, centerY, anchor, color);
		}
	}

	/**
	 * Draws only the ∞ symbol at the edge of a bar when the date is permanent but the bar is within view.
	 */
	private drawPermanentIndicator(edgeX: number, centerY: number, direction: 'left' | 'right', color: string, lb?: { left: number; right: number }): void {
		const isLeft = direction === 'left';
		const indicatorSpace = 18;
		if (lb) {
			const indicatorEdge = isLeft ? edgeX + indicatorSpace : edgeX - indicatorSpace;
			const overlaps = isLeft ? indicatorEdge >= lb.left - 4 : indicatorEdge <= lb.right + 4;
			if (overlaps) return;
		}
		const infX = isLeft ? edgeX + 6 : edgeX - 6;
		const anchor = isLeft ? 'start' : 'end';
		this.drawOverflowInfinity(infX, centerY, anchor, color);
	}

	/**
	 * Returns a map of segment → { left, right } pixel bounds for all visible bar-label texts.
	 */
	private getVisibleLabelBounds(): Map<GanttSegment, { left: number; right: number }> {
		const bounds = new Map<GanttSegment, { left: number; right: number }>();
		this.g.selectAll<SVGTextElement, GanttSegment>('text.bar-label').each(function (d) {
			if (this.getAttribute('display') === 'none') return;
			const bbox = this.getBBox();
			bounds.set(d, { left: bbox.x, right: bbox.x + bbox.width });
		});
		return bounds;
	}

	private drawOverflowArrow(tipX: number, centerY: number, size: number, direction: 'left' | 'right', color: string): void {
		const dx = direction === 'left' ? size : -size;
		this.g
			.append('path')
			.attr('class', 'overflow-arrow')
			.attr('d', `M${tipX + dx},${centerY - size} L${tipX},${centerY} L${tipX + dx},${centerY + size}`)
			.attr('stroke', color)
			.attr('stroke-width', 1.5)
			.attr('stroke-linecap', 'round')
			.attr('stroke-linejoin', 'round')
			.attr('fill', 'none')
			.attr('opacity', 0.7)
			.attr('pointer-events', 'none');
	}

	private drawOverflowInfinity(x: number, centerY: number, anchor: 'start' | 'end', color: string): void {
		this.g
			.append('text')
			.attr('class', 'overflow-infinity')
			.attr('x', x)
			.attr('y', centerY)
			.attr('text-anchor', anchor)
			.attr('dominant-baseline', 'central')
			.attr('font-size', '12px')
			.attr('fill', color)
			.attr('opacity', 0.7)
			.attr('pointer-events', 'none')
			.text('∞');
	}

	private drawOverlaps(barY: number): void {
		const scale = this.xScale();
		// Completely disable animation during drag (local or external)
		const useAnimation = !this.isDragging && !this.disableAnimation() && this.animationDuration() > 0;
		const duration = this.animationDuration();
		const barHeight = this.barHeight();

		// Remove existing overlap groups
		this.g.selectAll('g.overlap-group').remove();

		const overlapsData = this.overlaps();
		if (!overlapsData || overlapsData.length === 0) return;

		// Create a group for each overlap
		const overlapGroups = this.g
			.selectAll('g.overlap-group')
			.data(overlapsData)
			.enter()
			.append('g')
			.attr('class', 'overlap-group')
			.style('cursor', 'pointer')
			.on('mouseover', (event: MouseEvent, d: GanttSegment) => this.showTooltip(event, d))
			.on('mouseleave', () => this.hideTooltip())
			.on('click', (event: MouseEvent, d: GanttSegment) => this.onSegmentClick(event, d));

		// Get primary color for dynamic styling
		const primaryColor = this.getPrimaryColor();
		const edgeWidth = 12; // Width of the striped edge areas

		// Add the main overlap rectangle with light background (readable area)
		overlapGroups
			.append('rect')
			.attr('class', 'overlap')
			.attr('x', (d) => (useAnimation ? scale.range()[0] : scale(d.from)))
			.attr('width', (d) => (useAnimation ? 0 : Math.max(0, scale(d.to) - scale(d.from))))
			.attr('y', barY)
			.attr('height', barHeight)
			.attr('fill', `rgba(${primaryColor}, 0.12)`)
			.attr('stroke', `rgb(${primaryColor})`)
			.attr('stroke-width', 2.5)
			.attr('rx', 3)
			.attr('ry', 3);

		// Add striped pattern only at the LEFT edge
		overlapGroups
			.append('rect')
			.attr('class', 'overlap-edge-left')
			.attr('x', (d) => (useAnimation ? scale.range()[0] : scale(d.from)))
			.attr('width', (d) => {
				const totalWidth = useAnimation ? 0 : Math.max(0, scale(d.to) - scale(d.from));
				return Math.min(edgeWidth, totalWidth / 3);
			})
			.attr('y', barY)
			.attr('height', barHeight)
			.attr('fill', `url(#${this.overlapPatternId})`)
			.attr('rx', 3)
			.attr('ry', 0)
			.attr('pointer-events', 'none');

		// Add striped pattern only at the RIGHT edge
		overlapGroups
			.append('rect')
			.attr('class', 'overlap-edge-right')
			.attr('x', (d) => {
				const x = useAnimation ? scale.range()[0] : scale(d.from);
				const totalWidth = useAnimation ? 0 : Math.max(0, scale(d.to) - scale(d.from));
				const actualEdgeWidth = Math.min(edgeWidth, totalWidth / 3);
				return x + totalWidth - actualEdgeWidth;
			})
			.attr('width', (d) => {
				const totalWidth = useAnimation ? 0 : Math.max(0, scale(d.to) - scale(d.from));
				return Math.min(edgeWidth, totalWidth / 3);
			})
			.attr('y', barY)
			.attr('height', barHeight)
			.attr('fill', `url(#${this.overlapPatternId})`)
			.attr('rx', 0)
			.attr('ry', 3)
			.attr('pointer-events', 'none');

		// Add warning icon at the START of the overlap bar
		const iconSize = Math.min(16, barHeight - 4);
		const iconBgSize = iconSize + 6;
		const iconOffset = iconBgSize / 2 + 4; // Position from left edge

		// Background circle for the warning icon
		overlapGroups
			.append('circle')
			.attr('class', 'overlap-warning-bg')
			.attr('cx', (d) => {
				const x = useAnimation ? scale.range()[0] : scale(d.from);
				return x + iconOffset;
			})
			.attr('cy', barY + barHeight / 2)
			.attr('r', iconBgSize / 2)
			.attr('fill', 'white')
			.attr('stroke', `rgb(${primaryColor})`)
			.attr('stroke-width', 2)
			.attr('pointer-events', 'none');

		// Warning icon text (⚠)
		overlapGroups
			.append('text')
			.attr('class', 'overlap-warning-icon')
			.attr('x', (d) => {
				const x = useAnimation ? scale.range()[0] : scale(d.from);
				return x + iconOffset;
			})
			.attr('y', barY + barHeight / 2 + 1)
			.attr('text-anchor', 'middle')
			.attr('dominant-baseline', 'central')
			.attr('font-size', iconSize + 'px')
			.attr('font-weight', 'bold')
			.attr('fill', `rgb(${primaryColor})`)
			.attr('pointer-events', 'none')
			.text('⚠');

		// Only apply animation transition if needed
		if (useAnimation) {
			const animationDelay = (d: GanttSegment, i: number) => this.data().length * 20 + i * 20;

			// Animate main overlap rectangle
			overlapGroups
				.select('rect.overlap')
				.transition()
				.duration(duration)
				.delay(animationDelay)
				.attr('x', (d) => scale(d.from))
				.attr('width', (d) => Math.max(0, scale(d.to) - scale(d.from)));

			// Animate left edge
			overlapGroups
				.select('rect.overlap-edge-left')
				.transition()
				.duration(duration)
				.delay(animationDelay)
				.attr('x', (d) => scale(d.from))
				.attr('width', (d) => {
					const totalWidth = Math.max(0, scale(d.to) - scale(d.from));
					return Math.min(edgeWidth, totalWidth / 3);
				});

			// Animate right edge
			overlapGroups
				.select('rect.overlap-edge-right')
				.transition()
				.duration(duration)
				.delay(animationDelay)
				.attr('x', (d) => {
					const x = scale(d.from);
					const totalWidth = Math.max(0, scale(d.to) - scale(d.from));
					const actualEdgeWidth = Math.min(edgeWidth, totalWidth / 3);
					return x + totalWidth - actualEdgeWidth;
				})
				.attr('width', (d) => {
					const totalWidth = Math.max(0, scale(d.to) - scale(d.from));
					return Math.min(edgeWidth, totalWidth / 3);
				});

			// Animate icon background circle
			overlapGroups
				.select('circle.overlap-warning-bg')
				.transition()
				.duration(duration)
				.delay(animationDelay)
				.attr('cx', (d) => scale(d.from) + iconOffset);

			// Animate icon text
			overlapGroups
				.select('text.overlap-warning-icon')
				.transition()
				.duration(duration)
				.delay(animationDelay)
				.attr('x', (d) => scale(d.from) + iconOffset);
		}
	}

	private drawAxis(innerHeight: number, container: d3.Selection<SVGGElement, unknown, null, undefined>): void {
		const axisGenerator = d3.axisTop(this.xScale());
		const scale = this.xScale();

		const explicitStart = this.viewStartDate();
		const explicitEnd = this.viewEndDate();

		if (explicitStart && explicitEnd) {
			this.configureAxisTicks(axisGenerator, scale, explicitStart, explicitEnd);
		}

		const axisGroup = this.createAxisGroup(container, innerHeight);
		axisGroup.call(axisGenerator);

		this.styleYearLabels(axisGroup);

		if (explicitStart && explicitEnd) {
			this.drawYearSeparators(axisGroup, scale, explicitStart, explicitEnd);
		}

		if (this.enableAxisDrag()) {
			this.setupAxisDrag(axisGroup, scale);
		}
	}

	private configureAxisTicks(axisGenerator: d3.Axis<Date | d3.NumberValue>, scale: TimeScale, explicitStart: Date, explicitEnd: Date): void {
		const finalTickEndDate = new Date(explicitEnd.getFullYear(), explicitEnd.getMonth() + 1, 1);
		const defaultTicks = scale.ticks();
		const filteredDefaultTicks = defaultTicks.filter((tick) => tick.getTime() >= explicitStart.getTime() && tick.getTime() <= explicitEnd.getTime());
		const combinedTickDates = [explicitStart, ...filteredDefaultTicks, finalTickEndDate];

		const uniqueTimestamps = new Set(combinedTickDates.map((d) => d.getTime()));
		const finalTickValues = Array.from(uniqueTimestamps)
			.map((ts) => new Date(ts))
			.sort((a, b) => a.getTime() - b.getTime());

		axisGenerator.tickValues(finalTickValues);

		if (finalTickValues.length > 0) {
			const firstTickTime = finalTickValues[0].getTime();
			const lastTick = finalTickValues.at(-1);
			const lastTickTime = lastTick ? lastTick.getTime() : 0;

			axisGenerator.tickFormat((domainValue: Date | d3.NumberValue) => {
				if (!(domainValue instanceof Date)) {
					return '';
				}
				const currentTickTime = domainValue.getTime();
				const isEdgeTick = currentTickTime === firstTickTime || currentTickTime === lastTickTime;
				return isEdgeTick || domainValue.getMonth() === 0 ? formatYear(domainValue) : formatMonth(domainValue);
			});
		}
	}

	private createAxisGroup(
		container: d3.Selection<SVGGElement, unknown, null, undefined>,
		innerHeight: number
	): d3.Selection<SVGGElement, unknown, null, undefined> {
		const axisGroup = container.append('g').attr('class', 'x-axis');
		const yOffset = this.axisPosition() === 'top' ? -this.axisOffset() : innerHeight + this.axisOffset();
		axisGroup.attr('transform', `translate(0, ${yOffset})`);
		return axisGroup;
	}

	private styleYearLabels(axisGroup: d3.Selection<SVGGElement, unknown, null, undefined>): void {
		axisGroup.selectAll('.tick text').each(function (d) {
			const tickDate = d as Date;
			if (tickDate instanceof Date && tickDate.getMonth() === 0) {
				d3.select(this).classed('year-tick', true);
			}
		});
	}

	private drawYearSeparators(axisGroup: d3.Selection<SVGGElement, unknown, null, undefined>, scale: TimeScale, explicitStart: Date, explicitEnd: Date): void {
		const yearBoundaries = this.getYearBoundaries(explicitStart, explicitEnd);

		for (const yearDate of yearBoundaries) {
			const xPos = scale(yearDate);
			axisGroup
				.append('line')
				.attr('class', 'year-separator')
				.attr('x1', xPos)
				.attr('x2', xPos)
				.attr('y1', -5)
				.attr('y2', 10)
				.attr('stroke', '#999')
				.attr('stroke-width', 1.5)
				.attr('stroke-dasharray', '3,2');
		}
	}

	private getYearBoundaries(startDate: Date, endDate: Date): Date[] {
		const yearBoundaries: Date[] = [];
		let currentYear = startDate.getFullYear();
		const endYear = endDate.getFullYear();

		while (currentYear <= endYear) {
			const jan1 = new Date(currentYear, 0, 1);
			if (jan1 > startDate && jan1 <= endDate) {
				yearBoundaries.push(jan1);
			}
			currentYear++;
		}
		return yearBoundaries;
	}

	private setupAxisDrag(axisGroup: d3.Selection<SVGGElement, unknown, null, undefined>, scale: TimeScale): void {
		// Add a transparent rect over the axis area for better drag detection
		const scaleRange = scale.range();
		const axisHeight = 30; // Height of the clickable axis area

		// Create drag area rect
		axisGroup
			.insert('rect', ':first-child')
			.attr('class', 'axis-drag-area')
			.attr('x', scaleRange[0])
			.attr('y', -axisHeight / 2)
			.attr('width', scaleRange[1] - scaleRange[0])
			.attr('height', axisHeight)
			.attr('fill', 'transparent')
			.style('cursor', 'grab');

		// Add drag event listeners
		axisGroup.on('mousedown', (event: MouseEvent) => this.onAxisDragStart(event, scale)).style('cursor', 'grab');
	}

	private onAxisDragStart(event: MouseEvent, scale: TimeScale): void {
		event.preventDefault();
		event.stopPropagation();

		this.isDragging = true;
		this.dragStartX = event.clientX;
		this.dragScale = scale;
		const startDate = this.viewStartDate();
		const endDate = this.viewEndDate();
		this.dragStartDate = startDate ? new Date(startDate) : null;
		this.dragEndDate = endDate ? new Date(endDate) : null;
		this.lastEmitTime = 0;

		// Emit drag start event
		this.ngZone.run(() => {
			this.dragStart.emit();
		});

		// Change cursor to grabbing
		const svgElement = this.svg().nativeElement;
		svgElement.style.cursor = 'grabbing';
		const axisGroup = d3.select(svgElement).select('.x-axis');
		axisGroup.style('cursor', 'grabbing');

		// Add global event listeners (outside Angular zone for performance)
		this.ngZone.runOutsideAngular(() => {
			this.boundMouseMove = (e: MouseEvent) => this.onAxisDragMove(e);
			this.boundMouseUp = (e: MouseEvent) => this.onAxisDragEnd(e);

			document.addEventListener('mousemove', this.boundMouseMove);
			document.addEventListener('mouseup', this.boundMouseUp);
		});
	}

	private onAxisDragMove(event: MouseEvent): void {
		if (!this.isDragging || !this.dragStartDate || !this.dragEndDate || !this.dragScale) return;

		const now = Date.now();
		// Throttle emissions to avoid overwhelming the UI
		if (now - this.lastEmitTime < this.THROTTLE_MS) {
			return;
		}
		this.lastEmitTime = now;

		const deltaX = event.clientX - this.dragStartX;
		const scale = this.dragScale;

		// Convert pixel delta to time delta
		const domain = scale.domain();
		const range = scale.range();
		const pixelsPerMs = (range[1] - range[0]) / (domain[1].getTime() - domain[0].getTime());
		const msDelta = -deltaX / pixelsPerMs;

		// Calculate new dates
		const newStartDate = new Date(this.dragStartDate.getTime() + msDelta);
		const newEndDate = new Date(this.dragEndDate.getTime() + msDelta);

		// Emit the new date range live (run inside Angular zone)
		this.ngZone.run(() => {
			this.dateRangeShift.emit({ startDate: newStartDate, endDate: newEndDate });
		});
	}

	private onAxisDragEnd(event: MouseEvent): void {
		if (!this.isDragging) return;

		// Final emit to ensure we have the exact end position
		if (this.dragStartDate && this.dragEndDate && this.dragScale) {
			const deltaX = event.clientX - this.dragStartX;
			const scale = this.dragScale;

			const domain = scale.domain();
			const range = scale.range();
			const pixelsPerMs = (range[1] - range[0]) / (domain[1].getTime() - domain[0].getTime());
			const msDelta = -deltaX / pixelsPerMs;

			const newStartDate = new Date(this.dragStartDate.getTime() + msDelta);
			const newEndDate = new Date(this.dragEndDate.getTime() + msDelta);

			this.ngZone.run(() => {
				this.dateRangeShift.emit({ startDate: newStartDate, endDate: newEndDate });
			});
		}

		this.isDragging = false;
		this.dragStartX = 0;
		this.dragStartDate = null;
		this.dragEndDate = null;
		this.dragScale = null;

		// Emit drag end event
		this.ngZone.run(() => {
			this.dragEnd.emit();
		});

		// Restore cursor
		const svgElement = this.svg().nativeElement;
		svgElement.style.cursor = '';
		const axisGroup = d3.select(svgElement).select('.x-axis');
		axisGroup.style('cursor', 'grab');

		// Remove global event listeners
		this.removeDragListeners();
	}

	private showTooltip(event: MouseEvent, segment: GanttSegment): void {
		// Set the segment data that the template will use.
		// This now works for both regular and overlap segments.
		this.selectedSegment = segment;

		this.tooltipPosition = { x: `${event.clientX}px`, y: `${event.clientY}px` };
		this.isTooltipVisible = true;
		this.cdr.detectChanges();
	}

	private hideTooltip(): void {
		this.isTooltipVisible = false;
		this.cdr.detectChanges();
	}

	private relLuminanceFromRgb(r: number, g: number, b: number) {
		const srgb = [r, g, b].map((c) => c / 255).map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
		return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
	}

	private contrastRatio(hex1: string, hex2: string): number | null {
		const a = d3.color(hex1);
		const b = d3.color(hex2);
		if (!a || !b) return null;
		const ra = a.rgb();
		const rb = b.rgb();
		const L1 = this.relLuminanceFromRgb(ra.r, ra.g, ra.b);
		const L2 = this.relLuminanceFromRgb(rb.r, rb.g, rb.b);
		const lighter = Math.max(L1, L2);
		const darker = Math.min(L1, L2);
		return +((lighter + 0.05) / (darker + 0.05)).toFixed(2);
	}

	private readableTextColorForBackground(bgHex: string, minRatio = 4.5): string {
		const white = '#ffffff';
		const black = '#000000';
		const rW = this.contrastRatio(bgHex, white) ?? 0;
		const rB = this.contrastRatio(bgHex, black) ?? 0;
		if (rW >= minRatio && rW >= rB) return white;
		if (rB >= minRatio && rB > rW) return black;
		return rW >= rB ? white : black;
	}

	private dynamicTextColorForBackground(bgHex: string, minRatio = 4.5): string {
		if (!bgHex.startsWith('#')) {
			bgHex = ColorHelper.rgbStringToHex(bgHex);
		}
		return this.readableTextColorForBackground(bgHex, 10);
	}

	private useFallback(bgHex: string) {
		const white = '#ffffff';
		const black = '#000000';
		const rW = this.contrastRatio(bgHex, white) ?? 0;
		const rB = this.contrastRatio(bgHex, black) ?? 0;
		return rW >= rB ? white : black;
	}

	private clamp01(v: number) {
		return Math.max(0, Math.min(1, v));
	}

	protected readonly DateUtil = DateUtil;
	protected readonly Object = Object;
}
